import { useQuery } from 'react-query'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { ArrowLeft, RefreshCw, Users, AlertCircle } from 'lucide-react'
import ProtectedRoute from '../components/auth/ProtectedRoute'
import { AppHeader } from '../components'
import { apiService } from '../services/api'
import type { Team, TeamMember } from '../types'

interface MyTeamResponse {
  teamFeatureEnabled: boolean
  assigned?: boolean
  team?: Team
  members?: TeamMember[]
}

export default function TeamPage() {
  const { data, isLoading, isError, refetch } = useQuery<MyTeamResponse>(
    'myTeam',
    async () => {
      const response = await apiService.getMyTeam()
      if (response.error) {
        throw new Error(response.error.message)
      }
      const responseData = (response.data as any)?.data || response.data
      return responseData as MyTeamResponse
    },
    {
      retry: 1,
      useErrorBoundary: false,
    }
  )

  return (
    <ProtectedRoute>
      <Helmet>
        <title>My Team - FWO Pilot Portal</title>
        <meta name="description" content="View your team and team members" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <AppHeader activePage="team" />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <div className="mb-6">
            <Link
              to="/dashboard"
              className="inline-flex items-center text-fwo-blue hover:text-blue-700 transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Link>
          </div>

          {/* Content */}
          {isLoading && <LoadingSkeleton />}
          {isError && <ErrorBanner onRetry={() => refetch()} />}
          {!isLoading && !isError && data && <TeamContent data={data} />}
        </main>
      </div>
    </ProtectedRoute>
  )
}

function TeamContent({ data }: { data: MyTeamResponse }) {
  // Feature disabled
  if (!data.teamFeatureEnabled) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 text-lg">Team information is not currently available.</p>
      </div>
    )
  }

  // Not assigned to a team
  if (!data.assigned) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
        <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 text-lg">You have not been assigned to a team yet.</p>
      </div>
    )
  }

  // Team assigned - show members
  const team = data.team
  const members = data.members ?? []

  return (
    <>
      {/* Page Header */}
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          {team?.teamName ?? 'My Team'}
        </h2>
        <p className="text-gray-600">
          {members.length} {members.length === 1 ? 'member' : 'members'}
        </p>
      </div>

      {/* Member Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {members.map((member) => (
          <MemberCard key={member.userId} member={member} />
        ))}
      </div>
    </>
  )
}

function MemberCard({ member }: { member: TeamMember }) {
  const glider = [member.paragliderManufacturer, member.paragliderModel]
    .filter(Boolean)
    .join(' ')

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-start space-x-4">
      {/* Photo or Placeholder Avatar */}
      <div className="flex-shrink-0">
        {member.photoUrl ? (
          <img
            src={member.photoUrl}
            alt={member.fullName}
            className="h-16 w-16 rounded-full object-cover"
          />
        ) : (
          <PlaceholderAvatar />
        )}
      </div>

      {/* Member Info */}
      <div className="min-w-0 flex-1">
        <h3 className="text-sm font-semibold text-gray-900 truncate">
          {member.fullName}
        </h3>
        <p className="text-sm text-gray-500 mt-1">{member.gender}</p>
        {glider && (
          <p className="text-sm text-gray-500 truncate">{glider}</p>
        )}
        {member.faiNation && (
          <p className="text-sm text-gray-500">{member.faiNation}</p>
        )}
      </div>
    </div>
  )
}

function PlaceholderAvatar() {
  return (
    <svg
      className="h-16 w-16 rounded-full text-gray-300 bg-gray-100"
      fill="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
    </svg>
  )
}

function LoadingSkeleton() {
  return (
    <>
      {/* Header skeleton */}
      <div className="mb-8">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse mb-2" />
        <div className="h-5 w-24 bg-gray-200 rounded animate-pulse" />
      </div>

      {/* Card grid skeleton */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex items-start space-x-4"
          >
            <div className="h-16 w-16 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
            <div className="flex-1 space-y-2 pt-1">
              <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 w-20 bg-gray-200 rounded animate-pulse" />
              <div className="h-3 w-28 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

function ErrorBanner({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
        <p className="text-red-800">Team information could not be loaded.</p>
      </div>
      <button
        onClick={onRetry}
        className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50 transition-colors"
      >
        <RefreshCw className="h-4 w-4 mr-2" />
        Retry
      </button>
    </div>
  )
}
